import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { AiSearchResponseCreateStore } from "../../store";
import { runInAction } from "mobx";
import { InputReference } from "../../../common/controls";
import { InputText } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { EvaluationRating } from "../../../../api/graphql";
import { InputDateTime } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { UserCreate } from "../../../user/components/create/UserCreate";
import { UserCreateStore } from "../../../user/store";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { AiSearchToolCallCreate } from "../../../aiSearchToolCall/components/create/AiSearchToolCallCreate";
import { AiSearchToolCallCreateStore } from "../../../aiSearchToolCall/store";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type AiSearchResponseCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  conversationSelectedValue?: { displayId: string; id: string } | null;
  onConversationSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onConversationChange?: (value: string) => void;
  conversationOptions?: { displayId: string; id: string }[];
  conversationError?: string;
  querySelectedValue?: { displayId: string; id: string } | null;
  onQueryChange?: (value: string) => void;
  query: string;
  queryError?: string;
  responseSelectedValue?: { displayId: string; id: string } | null;
  onResponseChange?: (value: string) => void;
  response: string;
  responseError?: string;
  sourcesSelectedValue?: { displayId: string; id: string } | null;
  onSourcesChange?: (value: string) => void;
  sources: string;
  sourcesError?: string;
  explanationSelectedValue?: { displayId: string; id: string } | null;
  onExplanationChange?: (value: string) => void;
  explanation: string;
  explanationError?: string;
  tokenUsageSelectedValue?: { displayId: string; id: string } | null;
  onTokenUsageChange?: (value: string) => void;
  tokenUsage: string;
  tokenUsageError?: string;
  ratingError?: string;
  ratingOptions: { id: string; displayId: string }[];
  rating: EvaluationRating | "";
  onRatingChange?: (value: EvaluationRating | "") => void;
  evaluatedAtSelectedValue?: { displayId: string; id: string } | null;
  onEvaluatedAtChange?: (value: Date | null) => void;
  evaluatedAt: Date | null;
  evaluatedAtError?: string;
  evaluatedBySelectedValue?: { displayId: string; id: string } | null;
  onEvaluatedBySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onEvaluatedByChange?: (value: string) => void;
  evaluatedByOptions?: { displayId: string; id: string }[];
  evaluatedByError?: string;
  toolCallsOnRestore: (id: string) => void;
  toolCallsOnCommitChanges: (changes: GridChangeSet) => void;
  toolCallsValidateChanges: (changes: GridChangeSet) => boolean;
  toolCallsRows: CrudRow[];
  aiSearchToolCallCreateStore: AiSearchToolCallCreateStore;
  toolCallsError?: string;
  toolCallsVisibleColumns: GridColumn[];
  dummyCRUDRestoredObservable: number;
  aiSearchResponseCreateStore: AiSearchResponseCreateStore;
};

export const AiSearchResponseCreateForm: React.FC<AiSearchResponseCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onConversationSelected = () => {},
      conversationSelectedValue = null,
      conversationOptions = [],
      conversationError,
      onConversationChange = () => {},
      query = "",
      queryError,
      onQueryChange = () => {},
      response = "",
      responseError,
      onResponseChange = () => {},
      sources = "{}",
      sourcesError,
      onSourcesChange = () => {},
      explanation = "{}",
      explanationError,
      onExplanationChange = () => {},
      tokenUsage = "{}",
      tokenUsageError,
      onTokenUsageChange = () => {},
      ratingError,
      ratingOptions = [],
      rating = "" as EvaluationRating | "",
      onRatingChange = () => {},
      evaluatedAt = null,
      evaluatedAtError,
      onEvaluatedAtChange = () => {},
      onEvaluatedBySelected = () => {},
      evaluatedBySelectedValue = null,
      evaluatedByOptions = [],
      evaluatedByError,
      onEvaluatedByChange = () => {},
      toolCallsOnRestore,
      toolCallsOnCommitChanges,
      toolCallsValidateChanges,
      toolCallsRows,
      aiSearchToolCallCreateStore,
      toolCallsError,
      toolCallsVisibleColumns,
      dummyCRUDRestoredObservable,
      aiSearchResponseCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      if (aiSearchToolCallCreateStore.status === "uninitialized") {
        aiSearchToolCallCreateStore.initialize();
      }

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="AiSearchResponse"
              mode="create"
              values={{}}
              options={{
                conversation: conversationOptions,
                evaluatedBy: evaluatedByOptions,
              }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "conversation":
                    onConversationSelected?.(valor);
                    break;
                  case "query":
                    onQueryChange?.(valor);
                    break;
                  case "response":
                    onResponseChange?.(valor);
                    break;
                  case "sources":
                    onSourcesChange?.(valor);
                    break;
                  case "explanation":
                    onExplanationChange?.(valor);
                    break;
                  case "tokenUsage":
                    onTokenUsageChange?.(valor);
                    break;
                  case "rating":
                    onRatingChange?.(valor);
                    break;
                  case "evaluatedAt":
                    onEvaluatedAtChange?.(valor);
                    break;
                  case "evaluatedBy":
                    onEvaluatedBySelected?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={aiSearchResponseCreateStore.getCardErrors("null")}
              title={langStore.groupAiSearchResponseLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="conversation"
                  label={langStore.AiSearchResponseConversationLabel}
                  autoFocus
                  error={Boolean(conversationError)}
                  helperText={
                    conversationError ||
                    aiSearchResponseCreateStore.warnings["conversation"]
                      ?.message
                  }
                  selectedValue={conversationSelectedValue}
                  onChange={(value: string) => onConversationChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onConversationSelected(value)
                  }
                  options={conversationOptions}
                  canCreate={false}
                />

                <InputText
                  value={query}
                  error={Boolean(queryError)}
                  warning={
                    !queryError &&
                    Boolean(aiSearchResponseCreateStore.warnings["query"])
                  }
                  helperText={
                    queryError ||
                    aiSearchResponseCreateStore.warnings["query"]?.message
                  }
                  label={langStore.AiSearchResponseQueryLabel}
                  onChange={(value: string) => onQueryChange(value)}
                />

                <InputText
                  value={response}
                  error={Boolean(responseError)}
                  warning={
                    !responseError &&
                    Boolean(aiSearchResponseCreateStore.warnings["response"])
                  }
                  helperText={
                    responseError ||
                    aiSearchResponseCreateStore.warnings["response"]?.message
                  }
                  label={langStore.AiSearchResponseResponseLabel}
                  onChange={(value: string) => onResponseChange(value)}
                />

                <InputCodeEditor
                  value={sources}
                  error={Boolean(sourcesError)}
                  helperText={
                    sourcesError ||
                    aiSearchResponseCreateStore.warnings["sources"]?.message
                  }
                  label={langStore.AiSearchResponseSourcesLabel}
                  onChange={(value: string) => onSourcesChange(value)}
                  language="json"
                />

                <InputCodeEditor
                  value={explanation}
                  error={Boolean(explanationError)}
                  helperText={
                    explanationError ||
                    aiSearchResponseCreateStore.warnings["explanation"]?.message
                  }
                  label={langStore.AiSearchResponseExplanationLabel}
                  onChange={(value: string) => onExplanationChange(value)}
                  language="json"
                />

                <InputCodeEditor
                  value={tokenUsage}
                  error={Boolean(tokenUsageError)}
                  helperText={
                    tokenUsageError ||
                    aiSearchResponseCreateStore.warnings["tokenUsage"]?.message
                  }
                  label={langStore.AiSearchResponseTokenUsageLabel}
                  onChange={(value: string) => onTokenUsageChange(value)}
                  language="json"
                />
                <InputReference
                  id="rating"
                  error={Boolean(ratingError)}
                  helperText={
                    ratingError ||
                    aiSearchResponseCreateStore.warnings["rating"]?.message
                  }
                  label={langStore.AiSearchResponseRatingLabel}
                  options={ratingOptions}
                  nullText={langStore.optionalFieldHint}
                  selectedValue={
                    rating
                      ? {
                          id: rating,
                          displayId:
                            ratingOptions.find(
                              (o: { id: string }) => o.id === rating
                            )?.displayId || rating,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onRatingChange(
                      (value?.id as EvaluationRating | "") ||
                        ("" as EvaluationRating | "")
                    )
                  }
                />
                <InputDateTime
                  value={evaluatedAt}
                  error={Boolean(evaluatedAtError)}
                  warning={
                    !evaluatedAtError &&
                    Boolean(aiSearchResponseCreateStore.warnings["evaluatedAt"])
                  }
                  helperText={
                    evaluatedAtError ||
                    aiSearchResponseCreateStore.warnings["evaluatedAt"]?.message
                  }
                  label={langStore.AiSearchResponseEvaluatedAtLabel}
                  nullText={langStore.optionalFieldHint}
                  onChange={(value: Date | null | null) =>
                    onEvaluatedAtChange(value)
                  }
                />
                <InputReference
                  id="evaluatedBy"
                  label={langStore.AiSearchResponseEvaluatedByLabel}
                  error={Boolean(evaluatedByError)}
                  helperText={
                    evaluatedByError ||
                    aiSearchResponseCreateStore.warnings["evaluatedBy"]?.message
                  }
                  selectedValue={evaluatedBySelectedValue}
                  onChange={(value: string) => onEvaluatedByChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onEvaluatedBySelected(value)
                  }
                  options={evaluatedByOptions}
                  nullText={langStore.optionalFieldHint}
                  canCreate={rootStore.authStore.hasPermissions("user_create")}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new UserCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <UserCreate
                          userCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewUser"
                      cancelMethod="cancelNewUser"
                    />
                  )}
                />
                <ItemsCRUDCompact
                  label={langStore.AiSearchResponseToolCallsLabel}
                  showLabel={true}
                  rows={toolCallsRows}
                  columns={toolCallsVisibleColumns}
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() =>
                        aiSearchResponseCreateStore.toolCallsLoadRow(row)
                      );
                    }
                    return (
                      <AiSearchToolCallCreate
                        aiSearchToolCallCreateStore={
                          aiSearchToolCallCreateStore
                        }
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="md"
                  onRestore={(id: string) => toolCallsOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    toolCallsOnCommitChanges(changes)
                  }
                  validate={(changes: GridChangeSet) =>
                    toolCallsValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={toolCallsError}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
